import { formatDate } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { AddressArea } from 'app/models/addressarea.interface';
import { saveAs } from 'file-saver';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { utils, WorkBook, WorkSheet, write } from 'xlsx';
import { GoogleMapsComponent } from '../google-maps/google-maps.component';
import { Region } from '../region.class';
import { AddressService } from '../services/address/address.service';
import { ConfirmDialogService } from '../services/confirm-dialog-service/confirm-dialog.service';
import { PageTitleService } from '../services/page-title/page-title.service';
import { Settings } from '../settings.class';
import { Utils } from '../utils.class';
import { AddressSearchResult, LocationService } from '../workarea/shared/location.service';

@Component({
    selector: 'app-addresses-map',
    templateUrl: './addresses-map.component.html',
    styleUrls: ['./addresses-map.component.scss'],
    standalone: false,
})
export class AddressesMapComponent extends GoogleMapsComponent implements OnInit, OnDestroy, AfterViewInit {
    static limitBuildings = 2500;
    static warningBuildings = 100;
    Utils = Utils;
    Settings = Settings;

    loading = false;
    areas: AddressArea[] = [];
    polygonOptions = {
        clickable: false,
        strokeColor: '#0F0064',
        fillColor: '#85D1E4',
        fillOpacity: 0.35,
        strokeWeight: 2,
    };
    drawingArea: AddressArea;
    isPanelOpen = true;
    fcSearch = new FormControl('');
    addressSearch: Observable<AddressSearchResult[]> = null;
    private getBuildingsEmitter = new EventEmitter();

    private buildings$: Subscription;
    private readonly MAP_PADDING = 20;

    constructor(
        private locationService: LocationService,
        private addressService: AddressService,
        protected confirmDialogService: ConfirmDialogService,
        protected cdref: ChangeDetectorRef,
        private pageTitleService: PageTitleService
    ) {
        super(confirmDialogService, cdref);
    }

    showAddress(value: any) {
        return value ? value['name'] : '';
    }

    goToAddress(val: MatAutocompleteSelectedEvent) {
        // Click on the adress in the autocomplete
        const coords = val.option.value;
        this.zoomCenter(18, coords);
        this.openPanel();
    }

    openPanel() {
        this.isPanelOpen = true;
    }

    closePanel() {
        this.isPanelOpen = false;
    }

    togglePanel() {
        this.isPanelOpen = !this.isPanelOpen;
    }

    onSearchFocus() {
        if ((this.fcSearch.value ?? '').toString().trim()) {
            return;
        }

        this.fcSearch.setValue('');
    }

    ngOnInit() {
        this.pageTitleService.setTitle('Adressen');
        super.ngOnInit();

        this.subscriptions.add(this.addressService.getAddressAreas().subscribe(areas => (this.areas = areas.data)));

        this.addressSearch = this.locationService.searchAddresses(
            this.fcSearch.valueChanges,
            'AddressesMapComponent.addressSearch'
        );

        this.centerCountry();

        this.subscriptions.add(
            this.getBuildingsEmitter
                .pipe(
                    tap(() => {
                        if (this.drawingArea) {
                            this.drawingArea.loading = true;
                        }
                    })
                )
                .pipe(debounceTime(1500))
                .subscribe(() => this.loadBuildings())
        );
    }

    loadAdresses(area: AddressArea) {
        const downloadAddresses = (numbers: { identificatie: string; gebruiksdoelen: string[] }[]) => {
            area.loading = true;
            this.subscriptions.add(
                this.addressService.getAddresses(numbers.map(n => n.identificatie)).subscribe(
                    adressenResponse => {
                        const sheetArray = [];
                        adressenResponse.data.forEach(adres => {
                            const answerRow = {};
                            answerRow['Regel 1'] = adres.line_one;
                            answerRow['Regel 2'] = adres.line_two;
                            area.buildings
                                .find(b => +b.identificatie === +adres.id)
                                ?.gebruiksdoelen.forEach(
                                    (gebruiksdoel, i) => (answerRow['Gebruiksdoel ' + (i + 1)] = gebruiksdoel)
                                );

                            sheetArray.push(answerRow);
                        });

                        const worksheet: WorkSheet = utils.json_to_sheet(sheetArray);
                        worksheet['!cols'] = [
                            { width: 50 },
                            { width: 80 },
                            { width: 30 },
                            { width: 30 },
                            { width: 30 },
                        ];

                        const workbook: WorkBook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
                        const excelBuffer: any = write(workbook, { bookType: 'xlsx', type: 'array' });
                        const typeTitle = `${area.name}`.replace(/\s/g, '-').replace(/[^a-z0-9\-]/gi, '');
                        const filename = `Adressen_${typeTitle}_${formatDate(new Date(), 'yyyy-MMM-d_H-mm', 'nl')}`;
                        Settings.saveAsExcelFile(excelBuffer, filename.replace('.', ''));
                        area.loading = false;
                    },
                    () => (area.loading = true)
                )
            );
        };

        const region = new Region(area.exteriorRing);
        const numbers: { identificatie: string; gebruiksdoelen: string[] }[] = [];
        area.buildings?.forEach(building => {
            if (region.insideCustomer(building.locatie)) {
                numbers.push({ identificatie: building.identificatie, gebruiksdoelen: building.gebruiksdoelen });
            }
        });
        if (numbers.length > AddressesMapComponent.limitBuildings) {
            this.confirmDialogService.confirm(
                'Te veel gebouwen geselecteerd',
                `Het aantal gebouwen waarvoor adressen gedownload kunnen worden is gelimiteerd op ${AddressesMapComponent.limitBuildings}. Je probeert nu voor ${numbers.length} gebouwen de adressen te downloaden. Verklein het gebied.`,
                'Sluiten'
            );
        } else if (numbers.length > AddressesMapComponent.warningBuildings) {
            this.confirmDialogService
                .confirm(
                    'Adressen downloaden',
                    `Je staat op het punt om voor ${numbers.length} gebouwen de adressen te downloaden. Dit kan ${1 + Math.round(numbers.length / 60)} minuten duren. Weet je zeker dat je door wilt gaan?`,
                    'Adressen downloaden',
                    'Annuleren'
                )
                .then(() => downloadAddresses(numbers));
        } else {
            downloadAddresses(numbers);
        }
    }

    updateLocation(event: any, point: { lat: number; lng: number }) {
        point.lat = event.latLng.lat();
        point.lng = event.latLng.lng();
        this.drawingArea.exteriorRing = [...this.drawingArea.exteriorRing];
        this.getBuildingsEmitter.emit();
    }

    editArea(area: AddressArea) {
        this.areas.filter(a => a.drawing === true).forEach(drawingArea => (drawingArea.drawing = false));

        this.drawingArea = area;
        this.drawingArea.drawing = true;
        const region = new Region(this.drawingArea.exteriorRing);

        if (!region.hasPoints()) {
            this.centerCountry();
            return;
        }

        this.zoomCenter(region.bestMapView(window.innerWidth, window.innerHeight, this.MAP_PADDING), region.centroid());
    }

    addArea() {
        this.areas.filter(a => a.drawing === true).forEach(drawingArea => (drawingArea.drawing = false));

        const addressArea = {
            strokeColor: '#000000',
            exteriorRing: [],
            drawing: true,
        } as AddressArea;
        this.areas.unshift(addressArea);
        this.drawingArea = addressArea;
    }

    removeArea(area: AddressArea) {
        this.confirmDialogService
            .confirm('Adresgebied verwijderen', `Wil je het adresgebied ${area.name || ''} verwijderen?`, 'ja', 'nee')
            .then(() => {
                area.drawing = false;
                if (area.id) {
                    this.subscriptions.add(
                        this.addressService
                            .deleteAddressArea(area.id)
                            .subscribe(() => this.areas.splice(this.areas.indexOf(area), 1))
                    );
                } else {
                    this.areas.splice(this.areas.indexOf(area), 1);
                }
            });
    }

    saveArea(area: AddressArea) {
        if (!area.name) {
            return;
        }
        this.subscriptions.add(
            this.addressService.saveAddressArea(area).subscribe(areaResponse => {
                area.id = areaResponse.data.id;
                area.updated_at = areaResponse.data.updated_at;
            })
        );
    }

    loadBuildings() {
        const area = this.areas.find(a => a.drawing);
        if (area.exteriorRing.length > 3) {
            area.loading = true;
            const latitudes = area.exteriorRing.map(c => c.lat);
            const longitudes = area.exteriorRing.map(c => c.lng);
            const corners = [
                { lat: Math.min(...latitudes), lng: Math.min(...longitudes) },
                { lat: Math.max(...latitudes), lng: Math.max(...longitudes) },
            ];

            this.buildings$?.unsubscribe();

            this.buildings$ = this.addressService.getBuildings(corners).subscribe(buildingsResponse => {
                const region = new Region(area.exteriorRing);
                area.buildings = [];
                buildingsResponse.data.forEach(building => {
                    if (region.insideCustomer(building.locatie)) {
                        area.buildings.push(building);
                    }
                });
                this.saveArea(area);
                this.drawingArea.loading = false;
            });
        }
    }

    addPointToPolygon(e: any) {
        const area = this.areas.find(a => a.drawing === true);
        if (!area) {
            this.confirmDialogService.confirm(
                'Selecteer eerst een gebied',
                'Selecteer eerst een gebied of voeg een nieuw gebied toe.',
                'Sluiten'
            );
            return false;
        }
        if (!area.name) {
            this.subscriptions.add(
                this.locationService
                    .getLocations(e.latLng.lat(), e.latLng.lng(), 'AddressesMapComponent.addPointToPolygon')
                    .subscribe(response => {
                        const locality = response[0].address_components.find((c: any) =>
                            c.types.includes('locality')
                        )?.long_name;
                        const route = response[0].address_components.find((c: any) =>
                            c.types.includes('route')
                        )?.long_name;

                        if (locality && route) {
                            area.name = `${locality}, ${route}`;
                        } else {
                            area.name = response[0].formatted_address;
                        }
                    })
            );
        }

        area.exteriorRing = area.exteriorRing || [];

        area.exteriorRing.push({
            lat: e.latLng.lat(),
            lng: e.latLng.lng(),
        });
        this.drawingArea.exteriorRing = [...this.drawingArea.exteriorRing];
        this.getBuildingsEmitter.emit();
    }

    removePointFromPolygon(e: any) {
        if (!this.drawingArea) {
            return;
        }

        if (!this.drawingArea.name) {
            this.subscriptions.add(
                this.locationService
                    .getLocations(e.lat, e.lng, 'AddressesMapComponent.removePointFromPolygon')
                    .subscribe(response => {
                        const locality = response[0].address_components.find((c: any) =>
                            c.types.includes('locality')
                        )?.long_name;
                        const route = response[0].address_components.find((c: any) =>
                            c.types.includes('route')
                        )?.long_name;

                        if (locality && route) {
                            this.drawingArea.name = `${locality}, ${route}`;
                        } else {
                            this.drawingArea.name = response[0].formatted_address;
                        }
                    })
            );
        }

        if (!this.drawingArea.exteriorRing || this.drawingArea.exteriorRing.length < 3) {
            return;
        }

        const closest = this.drawingArea.exteriorRing.reduce((closest, p) => {
            const dist = Math.hypot(p.lat - e.latLng.lat(), p.lng - e.latLng.lng());
            const closestDist = Math.hypot(closest.lat - e.latLng.lat(), closest.lng - e.latLng.lng());
            return dist < closestDist ? p : closest;
        }, this.drawingArea.exteriorRing[0]);

        if (closest) {
            this.drawingArea.exteriorRing.splice(this.drawingArea.exteriorRing.indexOf(closest), 1);
        }
        this.drawingArea.exteriorRing = [...this.drawingArea.exteriorRing];
        this.getBuildingsEmitter.emit();
    }

    ngOnDestroy(): void {
        this.buildings$?.unsubscribe();
        super.ngOnDestroy();
    }

    downloadAsPdf(area: AddressArea) {
        area.loading = true;
        this.addressService.getAddressAreaPdf(area.id).subscribe(
            blob => {
                const typeTitle = `${area.name}`.replace(/\s/g, '_').replace(/[^a-z0-9\_]/gi, '');
                saveAs(blob, `adressen_${typeTitle}.pdf`);
                area.loading = false;
            },
            () => (area.loading = false)
        );
    }
}
